/**
 * ChatPanel component displays the chat conversation and message input.
 * Shows on the right side of the Test page (User View).
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useUpdateConversationMutation } from "@financial-advisory/data-access/financial-advisory-api-service";
import { useBranding } from "@targeted-support/ui/contexts";
import {
  ChevronDown,
  ClipboardCheck,
  FileText,
  Info,
  Loader2,
  Play,
  Settings,
  Square,
} from "lucide-react";
import { cn } from "@unlikelyai-magic/utils";
import { ChatInput } from "./ChatInput";
import { ConversationItem } from "./ConversationItem";
import { EmptyChatPlaceholder } from "./EmptyChatPlaceholder";
import { InitialMessage } from "./InitialMessage";
import type {
  ChatMode,
  ConversationPair,
  InitialMessage as InitialMessageType,
  ScrollRequest,
} from "./types";

interface ChatPanelProps {
  initialMessages?: InitialMessageType[];
  conversations: ConversationPair[];
  selectedIndex: number | null;
  onSelectMessage: (index: number) => void;
  scrollRequest: ScrollRequest | null;
  /**
   * When off, the chat holds its place as new messages arrive. The reader has
   * gone back to look at an earlier turn, so scrolling them away would undo it.
   */
  autoScrollToLatest?: boolean;
  onSendMessage: (message: string) => void;
  isLoading: boolean;
  isComplete: boolean;
  sessionId?: string;
  onNewChat?: () => void;
  shouldTerminateEarly?: boolean;
  onSetShouldTerminateEarly?: (value: boolean) => void;
  executeOutputGuardrails?: boolean;
  onSetExecuteOutputGuardrails?: (value: boolean) => void;
  executeInputGuardrails?: boolean;
  onSetExecuteInputGuardrails?: (value: boolean) => void;
  chatMode?: ChatMode;
  onStartSimulation?: () => void;
  onStopSimulation?: () => void;
  onContinueSimulation?: () => void;
  onSwitchToManual?: () => void;
  onShowAuditLog?: () => void;
}

export const ChatPanel = ({
  initialMessages = [],
  conversations,
  selectedIndex,
  onSelectMessage,
  scrollRequest,
  autoScrollToLatest = true,
  onSendMessage,
  isLoading,
  isComplete,
  onNewChat,
  sessionId,
  shouldTerminateEarly = false,
  onSetShouldTerminateEarly,
  executeOutputGuardrails = true,
  onSetExecuteOutputGuardrails,
  executeInputGuardrails = true,
  onSetExecuteInputGuardrails,
  chatMode = "manual",
  onStartSimulation,
  onStopSimulation,
  onContinueSimulation,
  onSwitchToManual,
  onShowAuditLog,
}: ChatPanelProps) => {
  const { name } = useBranding();
  const { user } = useAuth0();
  // The run toggles are a demo control rather than something a customer should
  // see, so they stay behind the same check as the Evaluation page.
  const canConfigureRun = user?.email?.endsWith("@unlikely.ai") ?? false;
  const [inputValue, setInputValue] = useState("");
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [isConfigExpanded, setIsConfigExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const inputRef = useRef<HTMLInputElement>(null);
  const prevIsLoadingRef = useRef<boolean>(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [updateConversation] = useUpdateConversationMutation();

  const hasStartedConversation = conversations.some(
    (conv) => conv.userMessage?.trim() !== ""
  );
  const isSimulating = chatMode === "simulating" || chatMode === "cancelling";

  useEffect(() => {
    if (scrollRequest !== null) {
      const itemElement = itemRefs.current.get(scrollRequest.index);
      if (itemElement) {
        itemElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [scrollRequest]);

  useEffect(() => {
    if (prevIsLoadingRef.current && !isLoading && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
    prevIsLoadingRef.current = isLoading;
  }, [isLoading]);

  const lastConversation = conversations[conversations.length - 1];
  useLayoutEffect(() => {
    if (!autoScrollToLatest) return;
    if (!containerRef.current) return;
    if (!bottomRef.current) return;
    if (initialMessages.length === 0 && conversations.length === 0) return;
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    });
  }, [
    autoScrollToLatest,
    initialMessages.length,
    conversations.length,
    lastConversation?.id,
    lastConversation?.systemResponse,
    lastConversation?.isLoadingResponse,
    isLoading,
  ]);

  const handleSendMessage = () => {
    onSendMessage(inputValue.trim());
    setInputValue("");
  };

  const hasContent = initialMessages.length > 0 || conversations.length > 0;

  const hasCompletedResponse = conversations.some(
    (conv) => !conv.isLoadingResponse && conv.systemResponse.trim() !== ""
  );

  const handleNewChat = () => {
    onNewChat?.();
  };

  const handleFeedbackClick = () => {
    setShowFeedbackForm(!showFeedbackForm);
  };

  const handleFeedbackSubmit = (): void => {
    if (feedbackText.trim() && sessionId) {
      updateConversation({
        session_id: sessionId,
        feedback: feedbackText.trim(),
      });
      setFeedbackText("");
      setShowFeedbackForm(false);
    }
  };

  return (
    <div className="flex h-[100%] flex-col rounded-lg border border-brand-border bg-brand-card">
      {/* Header - the customer's own agent, named as theirs */}
      <div className="flex items-center gap-2.5 border-b border-brand-border px-4 py-3">
        <span
          className="h-2 w-2 flex-none rounded-sm bg-brand-primary"
          aria-hidden={true}
        />
        <h2 className="text-sm font-medium text-brand-muted">
          {name} Assistant
        </h2>
        <span className="text-xs text-brand-muted">the bank&rsquo;s own agent</span>
        <span className="flex-1" />
        <div className="flex items-center gap-3">
          {canConfigureRun && !isComplete && (
            <button
              type="button"
              onClick={() => setIsConfigExpanded(!isConfigExpanded)}
              className="flex items-center gap-1.5 text-xs text-brand-muted hover:text-brand-foreground transition-colors"
            >
              <Settings className="h-3.5 w-3.5" />
              <span>Config</span>
              <ChevronDown className={cn("h-3 w-3 transition-transform", isConfigExpanded && "rotate-180")} />
            </button>
          )}
          {onShowAuditLog && hasStartedConversation && (
            <button
              type="button"
              onClick={onShowAuditLog}
              className="flex items-center gap-1.5 text-xs text-brand-muted transition-colors hover:text-brand-foreground"
            >
              <FileText className="h-3.5 w-3.5" />
              Audit log
            </button>
          )}
          {hasCompletedResponse && (
            <button
              type="button"
              onClick={handleFeedbackClick}
              disabled={!sessionId}
              className="flex items-center gap-1.5 text-xs text-brand-muted hover:text-brand-foreground transition-colors"
            >
              Add feedback
            </button>
          )}
        </div>
      </div>

      {/* Chat Config Section */}
      {isConfigExpanded && canConfigureRun && !isComplete && (
        <div className="border-b border-brand-border px-4 py-3 bg-brand-primary/5 space-y-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-muted">
            Chat Configuration
          </p>

          {onSetShouldTerminateEarly && (
            <label className="flex items-center justify-between gap-2 text-xs text-brand-foreground">
              <div className="flex items-center gap-1.5">
                <span>Early Provision of Recommendations</span>
                <span className="relative inline-flex group">
                  <Info className="w-3.5 h-3.5 text-brand-muted/60 cursor-help" />
                  <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 px-3 py-2 text-xs text-left normal-case tracking-normal bg-gray-900 text-white rounded-lg shadow-lg z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                    When enabled, product recommendations are surfaced as soon as they are identified and the conversation ends early. When disabled, all products are fully assessed before recommendations are presented. This setting is locked after the first message.
                  </span>
                </span>
                {(hasStartedConversation || isSimulating) && (
                  <span className="text-[10px] text-brand-muted italic">(locked)</span>
                )}
              </div>
              <select
                value={shouldTerminateEarly ? "yes" : "no"}
                onChange={(e) => onSetShouldTerminateEarly(e.target.value === "yes")}
                disabled={hasStartedConversation || isSimulating}
                className={cn(
                  "px-2 py-1 text-xs rounded-md border border-brand-border bg-brand-card text-brand-foreground",
                  (hasStartedConversation || isSimulating) && "opacity-50 cursor-not-allowed"
                )}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </label>
          )}

          {onSetExecuteOutputGuardrails && (
            <label className="flex items-center justify-between gap-2 text-xs text-brand-foreground">
              <div className="flex items-center gap-1.5">
                <span>Execute Output Guardrails</span>
                <span className="relative inline-flex group">
                  <Info className="w-3.5 h-3.5 text-brand-muted/60 cursor-help" />
                  <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 px-3 py-2 text-xs text-left normal-case tracking-normal bg-gray-900 text-white rounded-lg shadow-lg z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                    When enabled, output guardrails are evaluated against each agent response. This setting can be changed on a turn-by-turn basis.
                  </span>
                </span>
                {isSimulating && (
                  <span className="text-[10px] text-brand-muted italic">(locked)</span>
                )}
              </div>
              <select
                value={executeOutputGuardrails ? "yes" : "no"}
                onChange={(e) => onSetExecuteOutputGuardrails(e.target.value === "yes")}
                disabled={isSimulating}
                className={cn(
                  "px-2 py-1 text-xs rounded-md border border-brand-border bg-brand-card text-brand-foreground",
                  isSimulating && "opacity-50 cursor-not-allowed"
                )}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
          )}

          {onSetExecuteInputGuardrails && (
            <label className="flex items-center justify-between gap-2 text-xs text-brand-foreground">
              <div className="flex items-center gap-1.5">
                <span>Execute Input Guardrails</span>
                <span className="relative inline-flex group">
                  <Info className="w-3.5 h-3.5 text-brand-muted/60 cursor-help" />
                  <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 px-3 py-2 text-xs text-left normal-case tracking-normal bg-gray-900 text-white rounded-lg shadow-lg z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                    When enabled, input guardrails are evaluated against each user message. This setting can be changed on a turn-by-turn basis.
                  </span>
                </span>
                {isSimulating && (
                  <span className="text-[10px] text-brand-muted italic">(locked)</span>
                )}
              </div>
              <select
                value={executeInputGuardrails ? "yes" : "no"}
                onChange={(e) => onSetExecuteInputGuardrails(e.target.value === "yes")}
                disabled={isSimulating}
                className={cn(
                  "px-2 py-1 text-xs rounded-md border border-brand-border bg-brand-card text-brand-foreground",
                  isSimulating && "opacity-50 cursor-not-allowed"
                )}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
          )}
        </div>
      )}

      {/* Feedback Form */}
      {showFeedbackForm && (
        <div className="border-b border-brand-border px-4 py-3 bg-brand-primary/5">
          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="Enter your feedback..."
            className="w-full rounded-md border border-brand-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 resize-none bg-brand-card text-brand-foreground"
            rows={3}
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowFeedbackForm(false)}
              className="rounded-md px-3 py-1.5 text-sm text-brand-muted hover:bg-brand-primary/10 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleFeedbackSubmit()}
              disabled={!feedbackText.trim() || !sessionId}
              className="rounded-md bg-brand-primary px-3 py-1.5 text-sm text-white hover:bg-brand-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Submit
            </button>
          </div>
        </div>
      )}

      {/* Content area */}
      {!hasContent ? (
        <EmptyChatPlaceholder />
      ) : (
        <div
          ref={containerRef}
          className="flex-1 overflow-y-auto p-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        >
          <div className="space-y-4">
            {initialMessages.map((message) => (
              <InitialMessage key={message.id} message={message} />
            ))}
            {conversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                conversation={conversation}
                isSelected={selectedIndex === conversation.index}
                onSelect={() => onSelectMessage(conversation.index)}
                itemRef={(el) => {
                  if (el) itemRefs.current.set(conversation.index, el);
                }}
              />
            ))}
            <div ref={bottomRef} />
          </div>
        </div>
      )}

      {chatMode === "complete" || isComplete ? (
        <div className="border-t border-brand-border bg-brand-primary/5 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4 text-brand-primary" />
              <span className="text-sm font-medium text-brand-foreground">
                Conversation Complete
              </span>
            </div>
            <button
              type="button"
              onClick={handleNewChat}
              className="rounded-md bg-brand-primary px-3 py-1.5 text-sm text-white hover:bg-brand-primary-hover transition-colors"
            >
              New Chat
            </button>
          </div>
        </div>
      ) : chatMode === "idle" ? (
        <div className="border-t border-brand-border p-4">
          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={onStartSimulation}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-primary px-4 py-3 text-sm font-medium text-white hover:bg-brand-primary-hover transition-colors"
            >
              <Play className="h-4 w-4" />
              Simulate Chat
            </button>
            <button
              type="button"
              onClick={onSwitchToManual}
              className="text-xs text-brand-muted hover:text-brand-foreground transition-colors"
            >
              or chat manually
            </button>
          </div>
        </div>
      ) : chatMode === "simulating" ? (
        <div className="border-t border-brand-border p-4">
          <div className="flex items-center justify-between rounded-lg border border-brand-border bg-brand-card px-4 py-3">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-brand-primary" />
              <span className="text-sm text-brand-muted">
                Simulation running...
              </span>
            </div>
            <button
              type="button"
              onClick={onStopSimulation}
              className="flex items-center gap-1.5 rounded-md bg-red-500/10 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-500/20 transition-colors"
            >
              <Square className="h-3.5 w-3.5" />
              Stop
            </button>
          </div>
        </div>
      ) : chatMode === "cancelling" ? (
        <div className="border-t border-brand-border p-4">
          <div className="flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3">
            <Loader2 className="h-4 w-4 animate-spin text-amber-600 shrink-0" />
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-brand-foreground">
                Stopping after current response...
              </span>
              <span className="text-xs text-brand-muted">
                The simulation will end once the agent finishes responding.
              </span>
            </div>
          </div>
        </div>
      ) : chatMode === "paused" ? (
        <div className="border-t border-brand-border p-4">
          <div className="flex flex-col items-center gap-2">
            <p className="text-xs text-brand-muted">Simulation paused</p>
            <div className="flex w-full gap-2">
              <button
                type="button"
                onClick={onContinueSimulation}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-primary-hover transition-colors"
              >
                <Play className="h-3.5 w-3.5" />
                Continue Simulation
              </button>
              <button
                type="button"
                onClick={onSwitchToManual}
                className="flex flex-1 items-center justify-center rounded-lg border border-brand-border px-4 py-2.5 text-sm font-medium text-brand-foreground hover:bg-brand-primary/5 transition-colors"
              >
                Chat Manually
              </button>
            </div>
          </div>
        </div>
      ) : (
        <ChatInput
          value={inputValue}
          onChange={setInputValue}
          onSubmit={handleSendMessage}
          isLoading={isLoading}
          isDisabled={false}
          inputRef={inputRef}
        />
      )}
    </div>
  );
};
